import {
  FormControl,
  IconButton,
  InputAdornment,
  SxProps,
  TextField,
  Theme,
  Typography,
} from "@mui/material";
import React, { ChangeEvent, useState } from "react";
import { colorVariables, customValue } from "../../../utils/colorVariable";
import { Visibility, VisibilityOff } from "@mui/icons-material";

export interface ICustomInput {
  label: string;
  type?: React.HTMLInputTypeAttribute | undefined;
  name: string;
  placeholder?: string;
  value?: string | number;
  width?: any;
  onChange?: (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  error?: string;
  isDisabled?: boolean;
  startAdornment?: string;
  endAdornment?: string;
  sx?: SxProps<Theme>;
  required?: boolean;
  autoFocus?: boolean;
  inputRef?: React.Ref<HTMLInputElement>;
  textAlign?: any;
  hidden?: boolean;
  isNoBorder?: boolean;
  fontSize?: string;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  multiline?:boolean;
  rows?:number;
  onFocus?: (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  onBlur?: (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
}

export const CustomInput: React.FC<ICustomInput> = ({
  label,
  type,
  name,
  placeholder,
  value,
  error,
  isDisabled,
  width,
  startAdornment,
  sx,
  required = false,
  onChange,
  autoFocus,
  inputRef,
  endAdornment,
  textAlign,
  isNoBorder,
  fontSize,
  inputProps,
  multiline,
  rows,
  onFocus,
  onBlur,
}) => {
  const [showPassword, setShowPassword] = useState(false);

  const isPassword = type === "password";
  const resolvedType = isPassword ? (showPassword ? "text" : "password") : type;

  const handleTogglePassword = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <FormControl fullWidth sx={{ width: width ?? "100%" }}>
      <TextField

        error={!!error}
        helperText={error}
        size="small"
        id={name}
        type={resolvedType ?? "text"}
        name={name}
        placeholder={placeholder ?? label}
        autoComplete="off"
        required={required}
        autoFocus={autoFocus}
        inputRef={inputRef}
        multiline={multiline }
        rows={rows}
        value={
          type === undefined ||
          type === "text" ||
          type === "email" ||
          type === "tel" ||
          type === "password" ||
          type === "number"
            ? value ?? ""
            : 0
        }
        onKeyDown={(e) => {
          const arrowKeys = ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"];
          if (!arrowKeys.includes(e.key)) {
            e.stopPropagation();
          }
        }}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        disabled={isDisabled}
        variant="outlined"
        label={label ?? ""}
        sx={{
          input: {
            padding: "8px 8px",
            lineHeight: "1.5",
            verticalAlign: "middle",
          },
          borderRadius: customValue.borderRadius,
          "& .MuiOutlinedInput-root": {
            borderRadius: customValue.borderRadius,
            "& fieldset": {
              border: isNoBorder ? "none" : "",
            },
            "&:hover fieldset": {
              border: isNoBorder ? "none" : "",
            },
            "&.Mui-focused fieldset": {
              border: isNoBorder ? "none" : "",
            },
          },
          "& .MuiInputLabel-root": {
            fontSize: fontSize ?? "16px",
          },
          "& .MuiInputBase-input::placeholder": {
            fontSize: fontSize ?? "16px",
            color: "gray",
            fontStyle: "italic",
          },
          "&& .MuiInputBase-input.Mui-disabled": {
            color: colorVariables.disabledColor,
            WebkitTextFillColor: colorVariables.disabledColor,
            opacity: colorVariables.disabledOpacity,
          },
          "& .MuiInputBase-input.Mui-disabled::placeholder": {
            color: colorVariables.disabledColor,
            opacity: colorVariables.disabledOpacity,
            pb: 1,
          },
          ...sx,
        }}
        slotProps={{
          input: {
            startAdornment: startAdornment && (
              <InputAdornment position="start">
                <Typography sx={{ color: "black", fontWeight: "bold" }}>
                  {startAdornment}
                </Typography>
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {isPassword && (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleTogglePassword}
                      edge="end"
                      tabIndex={-1}
                      disabled={isDisabled}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )}
                {endAdornment && !isPassword && (
                  <InputAdornment position="end">
                    <Typography sx={{ color: "black", fontWeight: "bold" }}>
                      {endAdornment}
                    </Typography>
                  </InputAdornment>
                )}
              </>
            ),
          },
        }}
        inputProps={{
          ...inputProps,
          style: {
            textAlign: textAlign ?? "left",
            fontSize: fontSize ?? "18px",
            ...(inputProps?.style ?? {}), // Merge styles if inputProps.style exists
          },
        }}
      />
    </FormControl>
  );
};
